import * as THREE from "three";
import gsap from "gsap";
import { isWebglEnabled } from "./isWebglEnabled";
import { prefersReducedMotion } from "../utils/variables";
import { getRingGltf, constructedRingAsset, IPart, IStream, streamWidthScale, streamGapStartScale, streamGapEndScale, constructedRingStartRadius, constructedRingEndRadius, createDrumLogoMaterial, findMeshByObjectName, createRingPart, updateRingStreamWidth, clearStreams, getCustomLogoText, innerTextCopies, createTextSourceMesh, loadTextRingFont, outerTextCopies, getDefaultInnerRing } from "./logo-ring";

//////// ROOT
const introRootSelector = ".logo-intro";
const renderScale = 2;

//////// CAMERA
const introFov = 30;
const introEndZ = 1.45;
const introStartZ = 2.35;

//////// TIMING
const introInDurationMs = 2000;
const introOutDurationMs = 2000;
const minHoldMs = 0;
const maxHoldMs = 300;
const revealStartProgress = 0.35;
const revealGateProgress = 0.25;
const revealDurationSeconds = 0.8;
const revealEase = "power2.inOut";

//////// SPIN
const spinSpeed = 0.1;
const preIntroStartRotationDegrees = 40;
const introSpinTurns = 1;
const introOutSpinMultiplier = 0.9;
const introSwitchEnabled = true;
const introSwitchPoint: { type: string; value: number } = { type: "degrees", value: -30 };

//////// REVEAL POINTS
const outerCopyIntroRepeatProgress = 0.38;
const innerRingIntroDelaySeconds = 1;

//////// SCALE
const constructedRingStartScale = 0.4;
const constructedRingEndScale = 0.7;
const mobileLayoutBreakpoint = 900;
const mobileScaleFactor = 0.5;

//////// TILT
const baseTiltX = THREE.MathUtils.degToRad(-2.5);
const baseTiltZ = THREE.MathUtils.degToRad(3);
const secondaryRotationXAmount = THREE.MathUtils.degToRad(12);
const secondaryRotationZAmount = THREE.MathUtils.degToRad(8);
const secondaryRotationSpeed = 0.1;

//////// CONTENT
const introLogoColor = 0xffffff;
const outerRingName = "BROTHER3";
const outerRingCopies = 3;
const innerRingOptions: Record<string, { name: string; copies: number }> = {
    film: { name: "FILM3", copies: 6 },
    music: { name: "MUSIC3", copies: 5 },
};

function clamp01(value: number) {
    return Math.min(Math.max(value, 0), 1);
}

function easeIntro(t: number) {
    return t < 0.5 ? 64 * t * t * t * t * t * t * t : 1 - Math.pow(-2 * t + 2, 7) / 2;
}

function getClockwiseDegreesToTarget(startDegrees: number, targetDegrees: number) {
    const distance = (((startDegrees - targetDegrees) % 360) + 360) % 360;

    return distance === 0 ? 360 : distance;
}

function getIntroSwitchProgress() {
    if (!introSwitchEnabled) {
        return 0;
    }

    if (introSwitchPoint.type === "time") {
        return clamp01(introSwitchPoint.value > 1 ? introSwitchPoint.value / introInDurationMs : introSwitchPoint.value);
    }

    const clockwiseDegrees = getClockwiseDegreesToTarget(preIntroStartRotationDegrees, introSwitchPoint.value);
    const totalStartupDegrees = Math.max(Math.abs(introSpinTurns) * 360, 1);

    return clamp01(clockwiseDegrees / totalStartupDegrees);
}

function getIntroMatchedSpinAngle(progress: number, elapsedMs: number) {
    const startupDuration = introInDurationMs * 0.001;
    const elapsedSeconds = Math.min(elapsedMs, introInDurationMs) * 0.001;
    const restingSpinAngle = -spinSpeed * elapsedSeconds;
    const startupSpinAngleTarget = introSpinTurns * Math.PI * 2;
    const startupSpinBoost = Math.max(startupSpinAngleTarget - spinSpeed * startupDuration, 0);

    return restingSpinAngle - startupSpinBoost * easeIntro(progress);
}

function defaultWaitForReady(pageContainer: HTMLElement) {
    const viewportBottom = window.innerHeight;

    const visibleImages = Array.from(pageContainer.querySelectorAll<HTMLImageElement>("img")).filter(el => {
        if (el.loading === "lazy") {
            return false;
        }

        const rect = el.getBoundingClientRect();
        return rect.bottom > 0 && rect.top < viewportBottom;
    });

    const pending = visibleImages.map(el => (el.complete ? Promise.resolve() : el.decode().catch(() => undefined)));

    return Promise.all([document.fonts?.ready, ...pending]);
}

//////// MASK-IN

export function markIntroDone() {
    document.documentElement.classList.add("intro-done");
}

function clampContainer(pageContainer: HTMLElement) {
    gsap.set(pageContainer, { height: "100vh", overflow: "hidden", clipPath: "inset(50% round 3%)" });
}

function releaseContainer(pageContainer: HTMLElement) {
    gsap.set(pageContainer, { clearProps: "clipPath,height,overflow" });
}

function revealPageContainer(pageContainer: HTMLElement, onRevealGate?: () => void) {
    return new Promise<void>(resolve => {
        let tween: gsap.core.Tween | null = null;
        let hasFiredGate = false;

        tween = gsap.to(pageContainer, {
            clipPath: "inset(0% round 0%)",
            duration: revealDurationSeconds,
            ease: revealEase,
            onUpdate: () => {
                if (!hasFiredGate && (tween?.progress() ?? 0) >= revealGateProgress) {
                    hasFiredGate = true;
                    onRevealGate?.();
                }
            },
            onComplete: () => {
                markIntroDone();
                releaseContainer(pageContainer);
                onRevealGate?.();
                resolve();
            },
        });
    });
}

export default function playLogoIntro(pageContainer: HTMLElement, waitForReady?: (pageContainer: HTMLElement) => Promise<unknown>, onRevealGate?: () => void): Promise<void> {
    const root = document.querySelector<HTMLElement>(introRootSelector);

    if (!root || !isWebglEnabled() || prefersReducedMotion) {
        markIntroDone();
        releaseContainer(pageContainer);
        onRevealGate?.();
        return Promise.resolve();
    }

    clampContainer(pageContainer);
    root.hidden = false;

    return new Promise<void>(resolve => {
        const scene = new THREE.Scene();
        const clock = new THREE.Clock();
        const streams: IStream[] = [];
        const customLogoText = getCustomLogoText(pageContainer);
        const introSwitchProgress = getIntroSwitchProgress();

        const camera = new THREE.PerspectiveCamera(introFov, root.clientWidth / Math.max(root.clientHeight, 1), 0.001, 200);
        camera.position.set(0, 0, introStartZ);

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: "low-power" });
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.toneMapping = THREE.NoToneMapping;
        renderer.toneMappingExposure = 1;
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, renderScale));
        renderer.setSize(root.clientWidth, root.clientHeight);
        root.appendChild(renderer.domElement);

        const model = new THREE.Group();
        const modelSpin = new THREE.Group();

        modelSpin.rotation.y = THREE.MathUtils.degToRad(preIntroStartRotationDegrees);
        model.add(modelSpin);
        scene.add(model);

        let startTime: number | null = null;
        let holdStartTime: number | null = null;
        let outStartTime: number | null = null;
        let spinLastAngle = 0;
        let outSpinLastAngle = 0;
        let rafId = 0;
        let isReady = false;
        let hasStartedReveal = false;
        let isDisposed = false;
        let isMobileLayout = window.matchMedia(`(max-width: ${mobileLayoutBreakpoint}px)`).matches;

        void Promise.all([defaultWaitForReady(pageContainer), waitForReady?.(pageContainer)])
            .catch(() => undefined)
            .then(() => {
                isReady = true;
            });

        function onIntroResize() {
            isMobileLayout = window.matchMedia(`(max-width: ${mobileLayoutBreakpoint}px)`).matches;
            camera.aspect = root!.clientWidth / Math.max(root!.clientHeight, 1);
            camera.updateProjectionMatrix();
            renderer.setSize(root!.clientWidth, root!.clientHeight);
        }

        window.addEventListener("resize", onIntroResize);

        function startReveal() {
            if (hasStartedReveal) {
                return;
            }
            hasStartedReveal = true;
            void revealPageContainer(pageContainer, onRevealGate).then(resolve);
        }

        function dispose() {
            if (isDisposed) {
                return;
            }

            isDisposed = true;
            cancelAnimationFrame(rafId);
            window.removeEventListener("resize", onIntroResize);
            clearStreams(streams);
            renderer.forceContextLoss();
            renderer.dispose();
            renderer.domElement.remove();
            root!.hidden = true;
            startReveal();
        }

        function getParts(): IPart[] {
            const namespace = pageContainer.dataset.barbaNamespace || "";
            const innerRingOption = namespace === "work-detail-music" ? innerRingOptions.music : innerRingOptions[getDefaultInnerRing()];
            const outerCopies = customLogoText.outer ? outerTextCopies : outerRingCopies;

            return [
                {
                    name: outerRingName,
                    text: customLogoText.outer,
                    copies: outerCopies,
                    isInnerRing: false,
                    introRepeatCopies: outerCopies > 1,
                },
                {
                    ...innerRingOption,
                    text: customLogoText.inner,
                    copies: customLogoText.inner ? innerTextCopies : innerRingOption.copies,
                    isInnerRing: true,
                },
            ];
        }

        function buildRing(sourceScene: THREE.Group) {
            const group = new THREE.Group();

            getParts().forEach(part => {
                const sourceMesh = part.text ? createTextSourceMesh(part.text, `${part.name}_TEXT`, part.copies, part.isInnerRing, introLogoColor, renderer.capabilities.getMaxAnisotropy()) : findMeshByObjectName(sourceScene, part.name);

                if (!sourceMesh) {
                    console.error(`Missing mesh "${part.name}" in ${constructedRingAsset}`);
                    return;
                }

                const built = createRingPart(sourceMesh, part, constructedRingStartRadius, part.text ? sourceMesh.material : createDrumLogoMaterial(introLogoColor));

                streams.push(built.stream);
                group.add(built.group);
            });

            return group;
        }

        function updateVisibility(visualProgress: number) {
            const shouldRepeatCopies = visualProgress >= outerCopyIntroRepeatProgress;
            const hasPassedInnerRingDelay = visualProgress >= clamp01(innerRingIntroDelaySeconds / (introInDurationMs * 0.001));

            streams.forEach(stream => {
                if (stream.introRepeatCopies) {
                    stream.repeatCopiesVisible = shouldRepeatCopies;
                }

                stream.group.visible = stream.isInnerRing ? hasPassedInnerRingDelay : true;
            });
        }

        function updateRing(easedProgress: number) {
            const gapScale = THREE.MathUtils.lerp(streamGapStartScale, streamGapEndScale, easedProgress);
            const radius = THREE.MathUtils.lerp(constructedRingStartRadius, constructedRingEndRadius, easedProgress);
            const scaleFactor = isMobileLayout ? mobileScaleFactor : 1;
            const overallScale = THREE.MathUtils.lerp(constructedRingStartScale * scaleFactor, constructedRingEndScale * scaleFactor, easedProgress);

            streams.forEach(stream => {
                updateRingStreamWidth(stream, streamWidthScale, gapScale, radius);
            });

            modelSpin.scale.setScalar(overallScale);
        }

        function updateTilt(now: number) {
            const time = now * -0.001 * secondaryRotationSpeed;

            model.rotation.x = baseTiltX + Math.sin(time) * secondaryRotationXAmount;
            model.rotation.z = baseTiltZ + Math.cos(time * 0.8) * secondaryRotationZAmount;
        }

        function updateCamera(introProgress: number) {
            const switchProgress = THREE.MathUtils.clamp(introSwitchProgress, 0, 0.95);
            const switchedProgress = clamp01((introProgress - switchProgress) / (1 - switchProgress));

            camera.position.z = THREE.MathUtils.lerp(introStartZ, introEndZ, easeIntro(switchedProgress));
            camera.updateProjectionMatrix();
        }

        function animate() {
            const now = performance.now();
            const delta = clock.getDelta();

            startTime ??= now;

            const introProgress = Math.min((now - startTime) / introInDurationMs, 1);

            if (introProgress < 1) {
                const spinAngle = getIntroMatchedSpinAngle(introProgress, now - startTime);

                modelSpin.rotation.y += spinAngle - spinLastAngle;
                spinLastAngle = spinAngle;

                updateVisibility(introProgress);
                updateRing(easeIntro(introProgress));
            } else {
                holdStartTime ??= now;

                if (outStartTime === null) {
                    const heldMs = now - holdStartTime;

                    if (heldMs >= minHoldMs && (isReady || heldMs >= maxHoldMs)) {
                        outStartTime = now;
                    }
                }

                if (outStartTime === null) {
                    //////// HOLD: page not ready yet, keep resting spin
                    modelSpin.rotation.y += -delta * spinSpeed;
                    updateVisibility(1);
                    updateRing(1);
                } else {
                    const outElapsedMs = Math.max(now - outStartTime, 0);
                    const outProgress = Math.min(outElapsedMs / introOutDurationMs, 1);
                    const outSpinAngle = getIntroMatchedSpinAngle(outProgress, outElapsedMs) * introOutSpinMultiplier;
                    const visualProgress = 1 - outProgress;

                    modelSpin.rotation.y += outSpinAngle - outSpinLastAngle;
                    outSpinLastAngle = outSpinAngle;

                    updateVisibility(visualProgress);
                    updateRing(easeIntro(visualProgress));

                    if (outProgress >= revealStartProgress) {
                        startReveal();
                    }

                    if (outProgress >= 1) {
                        dispose();
                        return;
                    }
                }
            }

            updateTilt(now);
            updateCamera(introProgress);

            renderer.render(scene, camera);
        }

        Promise.all([loadTextRingFont(Boolean(customLogoText.inner || customLogoText.outer)), getRingGltf()])
            .then(([, gltf]) => {
                modelSpin.add(buildRing(gltf.scene as THREE.Group));
                clock.getDelta();

                rafId = requestAnimationFrame(function tick() {
                    rafId = requestAnimationFrame(tick);
                    animate();
                });
            })
            .catch(error => {
                console.error(error);
                dispose();
            });
    });
}
